import { useCallback, useEffect, useState, useTransition } from 'react';
import type { Nullable } from 'corgi-types';

import { EventBusEvent } from 'common/enums/EventBus';
import { useMatchesViewport } from 'common/hooks/media';
import { useSubscriptionCallback } from 'common/hooks/subscription';
import type { GraphqlNavigationItemProps } from 'common/types/NavigationItem';
import type { DispatchState } from 'common/types/React';
import type { SideNavEvent, SideNavType } from 'common/utils/navigation';
import type { CurrentNavigation } from '../types';
import { getCascadingNavigationData, getNavigationData } from '../utils';
import { useFetchNavigation } from './useFetchNavigation';

interface UseHandlerOptions {
  type: SideNavType;
  isCascading: boolean;
  setCurrentNavigation: DispatchState<CurrentNavigation>;
  setSelectedItem: DispatchState<Nullable<number>>;
  fetchedNavigation: GraphqlNavigationItemProps;
  setIsOpen: (arg: boolean) => void;
}

const useCurrentNavigation = (
  fetchedNavigation: GraphqlNavigationItemProps,
  isCascading: boolean
) => {
  const [, startTransition] = useTransition();
  // Currently selected navigation items
  const [currentNavigation, setCurrentNavigation] = useState<CurrentNavigation>([]);
  // Initially selected item (from Top navigation)
  const [selectedItem, setSelectedItem] = useState<Nullable<number>>(null);

  const clickHandler = useCallback(
    (level: number) => event => {
      const itemId = Number.parseInt(event.currentTarget.dataset.itemId, 10);

      // We use transition to optimize rendering performance - it affects INP significantly.
      startTransition(() => {
        setCurrentNavigation([...currentNavigation.slice(0, level), itemId]);
      });
    },
    [currentNavigation]
  );

  const navigation = isCascading
    ? getCascadingNavigationData({
        currentNavigation,
        fetchedNavigation,
        selectedItem,
      })
    : getNavigationData(currentNavigation, fetchedNavigation);

  return {
    currentNavigation,
    setCurrentNavigation,
    clickHandler,
    selectedItem,
    setSelectedItem,
    navigation,
  };
};

const useHandlers = ({
  type,
  isCascading,
  setCurrentNavigation,
  setSelectedItem,
  fetchedNavigation,
  setIsOpen,
}: UseHandlerOptions) => {
  useSubscriptionCallback<SideNavEvent>(EventBusEvent.ToggleSideNav, data => {
    if (data.type && data.type !== type) return;

    setIsOpen(data.isOpen);
    if (data.selectedItem) {
      const index = fetchedNavigation.children.findIndex(item => item.name === data.selectedItem);
      setSelectedItem(index);
    }
  });

  const closeHandler = () => {
    if (isCascading) {
      setCurrentNavigation([]);
      setSelectedItem(null);
    }
    setIsOpen(false);
  };

  return { closeHandler };
};

export const useNavigation = ({
  initialNavigation,
  type,
  withSideNavTabs,
}: {
  initialNavigation: GraphqlNavigationItemProps;
  type: SideNavType;
  withSideNavTabs: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { fetchedNavigation, tabs, isLoading } = useFetchNavigation({
    initialNavigation,
    isOpen,
    type,
    withSideNavTabs,
  });

  const { matches } = useMatchesViewport();
  const isCascading = matches.matchesDesktop;

  const {
    currentNavigation,
    setCurrentNavigation,
    clickHandler,
    selectedItem,
    setSelectedItem,
    navigation,
  } = useCurrentNavigation(fetchedNavigation, isCascading);

  const { closeHandler } = useHandlers({
    type,
    isCascading,
    setCurrentNavigation,
    setSelectedItem,
    fetchedNavigation,
    setIsOpen,
  });

  useEffect(() => {
    if (!isOpen) {
      setCurrentNavigation([]);
      setSelectedItem(null);
    }
  }, [matches.matchesDesktop]);

  const navItemParent =
    selectedItem !== null ? fetchedNavigation.children[selectedItem].name : fetchedNavigation.name;

  return {
    isOpen,
    currentNavigation,
    closeHandler,
    setCurrentNavigation,
    selectedItem,
    setSelectedItem,
    navigation,
    fetchedNavigation,
    tabs,
    clickHandler,
    isCascading,
    navItemParent,
    isLoading,
  };
};
